<template>
  <span class="d-inline-flex align-center ga-2" data-testid="dca-flag-badges">
    <SoSkeleton v-if="loading" width="200" height="28" />

    <template v-else>
      <!--
        Opened downward: these sit level with the page title, and a tooltip above them
        is cut off by the nav masthead.
      -->
      <SoTooltip :text="allocationTooltip" location="bottom">
        <SoStatus
          :status="allocationStatus"
          data-testid="dca-flag-allocation"
        />
      </SoTooltip>

      <SoTooltip :text="psrTooltip" location="bottom">
        <SoStatus :status="psrStatus" data-testid="dca-flag-psr" />
      </SoTooltip>
    </template>
  </span>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import { Status } from "@/types/Status"
  import { FeaturesService } from "@/services/admin/FeaturesService"

  /**
   * Whether the config on this screen actually governs anything, and whether PSR
   * accounts are in scope.
   *
   * Without this the screen gives no clue whether you are editing live config or a
   * table nothing reads - the simplified allocation path is behind a flag, and while
   * that flag is off the job still uses the legacy chain-replay logic and ignores this
   * table entirely.
   *
   * Both are read-only on purpose. These are global flags: flipping either changes the
   * allocation job's behaviour for everyone, and PSR in particular is a
   * customer-vulnerability decision rather than a display preference. Changing them
   * stays on the features admin page, where the people who own them can see it - see
   * SO-32234 for enabling PSR placement properly.
   *
   * Rendered with SoStatus so they read as the same kind of chip as the Status column
   * on the sync-accounts table, rather than inventing a badge style for this screen.
   */
  const SIMPLIFIED_ALLOCATION_FLAG = "TMP_SO_31817_SIMPLIFIED_DCA_ALLOCATION"
  const PSR_PLACEMENT_FLAG = "DCA_ALLOCATE_PSR_ACCOUNTS"

  /** 'normal' is the green dot, 'default' the grey one. */
  const GREEN: Status["type"] = "normal"
  const GREY: Status["type"] = "default"

  export default defineComponent({
    components: { SoStatus, SoSkeleton, SoTooltip },
    computed: {
      /** undefined while the flag list is still loading, so the chips do not flicker. */
      simplifiedAllocation(): boolean | undefined {
        return FeaturesService.isFeatureActive(SIMPLIFIED_ALLOCATION_FLAG)
      },
      psrPlacement(): boolean | undefined {
        return FeaturesService.isFeatureActive(PSR_PLACEMENT_FLAG)
      },
      loading(): boolean {
        return (
          this.simplifiedAllocation === undefined ||
          this.psrPlacement === undefined
        )
      },
      allocationStatus(): Status {
        return this.simplifiedAllocation
          ? { type: GREEN, text: "Live" }
          : { type: GREY, text: "Not live" }
      },
      psrStatus(): Status {
        return this.psrPlacement
          ? { type: GREEN, text: "PSR included" }
          : { type: GREY, text: "PSR excluded" }
      },
      /**
       * Says the config "governs" allocations rather than that placements have used it.
       * The flag decides which path the job takes; it says nothing about whether a run
       * has happened since it was switched on.
       */
      allocationTooltip(): string {
        return this.simplifiedAllocation
          ? "The config below governs live DCA allocations."
          : "The config below is not being used for DCA allocations yet — the allocation job is still using the legacy logic."
      },
      /**
       * Deliberately says nothing about the feature flag behind this - that is
       * engineer-facing, and a P&C reader only needs to know whether PSR accounts are in
       * scope of the split they are looking at.
       */
      psrTooltip(): string {
        return this.psrPlacement
          ? "PSR accounts are being placed using the same split as non-PSR accounts in the same segment."
          : "Allocation of PSR accounts is off - the placement system currently skips them."
      },
    },
  })
</script>
